import React, { useEffect, useState } from 'react';
import { adminSessionFetch } from '../../utils/functionAuth';
import AgentHealthPanel from './AgentHealthPanel';
import GrowthFunnelPanel from './GrowthFunnelPanel';
import SupportViewPanel from './SupportViewPanel';
import AccountControlsPanel from './AccountControlsPanel';
import AnnouncementsPanel from './AnnouncementsPanel';
import FeatureFlagsPanel from './FeatureFlagsPanel';
import SupportInboxPanel from './SupportInboxPanel';
import LeaderboardsPanel from './LeaderboardsPanel';
import SystemHealthPanel from './SystemHealthPanel';

export const CEO_SEAT_TABS = [
  { id: 'ceo-health', label: 'Health' },
  { id: 'ceo-funnel', label: 'Funnel' },
  { id: 'ceo-support', label: 'Support view' },
  { id: 'ceo-accounts', label: 'Accounts' },
  { id: 'ceo-announcements', label: 'Announcements' },
  { id: 'ceo-flags', label: 'Flags' },
  { id: 'ceo-inbox', label: 'Inbox' },
  { id: 'ceo-leaders', label: 'Leaders' },
  { id: 'ceo-system', label: 'System' },
];

const ENDPOINTS = {
  'ceo-health': '/.netlify/functions/admin-agent-health',
  'ceo-funnel': '/.netlify/functions/admin-growth-funnel',
  'ceo-support': '/.netlify/functions/admin-support-view',
  'ceo-accounts': '/.netlify/functions/admin-account-controls',
  'ceo-announcements': '/.netlify/functions/admin-announcements',
  'ceo-flags': '/.netlify/functions/admin-feature-flags',
  'ceo-inbox': '/.netlify/functions/admin-support-inbox',
  'ceo-leaders': '/.netlify/functions/admin-leaderboards',
  'ceo-system': '/.netlify/functions/admin-system-health',
};

const INITIAL = {
  'ceo-support': { action: 'agents' },
  'ceo-accounts': { action: 'list' },
  'ceo-announcements': { action: 'list' },
  'ceo-flags': { action: 'list' },
  'ceo-inbox': { action: 'list' },
};

async function postCeo(tab, body) {
  const response = await adminSessionFetch(ENDPOINTS[tab], body || {});
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || 'Could not load this view');
    error.status = response.status;
    throw error;
  }
  return data;
}

export default function CeoSeat({ tab, colors, narrow, previewData }) {
  const [data, setData] = useState(previewData || null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (previewData) {
      setData(previewData);
      setError('');
      return undefined;
    }
    let live = true;
    setData(null);
    setError('');
    postCeo(tab, INITIAL[tab] || {})
      .then((next) => { if (live) setData(next); })
      .catch((err) => { if (live) setError(err.message || 'Could not load this view'); });
    return () => { live = false; };
  }, [tab, previewData]);

  async function onAction(body) {
    if (previewData) return { ok: true, ...(body || {}) };
    setBusy(true);
    try {
      const result = await postCeo(tab, body);
      if (body && (body.action === 'create' || body.action === 'end' || body.action === 'save' || body.action === 'update' || body.confirm)) {
        const fresh = await postCeo(tab, INITIAL[tab] || {});
        setData(fresh);
      }
      return result;
    } finally {
      setBusy(false);
    }
  }

  const shared = { colors, narrow, data, error, onAction, busy };
  if (tab === 'ceo-funnel') return <GrowthFunnelPanel {...shared} />;
  if (tab === 'ceo-support') return <SupportViewPanel {...shared} />;
  if (tab === 'ceo-accounts') return <AccountControlsPanel {...shared} />;
  if (tab === 'ceo-announcements') return <AnnouncementsPanel {...shared} />;
  if (tab === 'ceo-flags') return <FeatureFlagsPanel {...shared} />;
  if (tab === 'ceo-inbox') return <SupportInboxPanel {...shared} />;
  if (tab === 'ceo-leaders') return <LeaderboardsPanel {...shared} />;
  if (tab === 'ceo-system') return <SystemHealthPanel {...shared} />;
  return <AgentHealthPanel {...shared} />;
}
